// スタッフ向け業務通知イベント（ヘッダーベル / 通知一覧ページ）で共有する定数。
//
// イベントは API (staff/notification-events) が 1 イベント = 1 レコードで管理し、
// 既読もスタッフ個人単位でサーバ管理される。かつて存在した localStorage による
// アラート既読シグネチャ管理はこの仕組みへ置き換えられて廃止した。

/** API の StaffNotificationType (Prisma enum) と同値の文字列リテラル */
export const STAFF_NOTIFICATION_TYPES = [
  "TRIAL_APPLICATION",
  "EVENT_APPLICATION",
  "EVENT_CANCELLATION",
  "ADMISSION_REQUEST",
  "ATTENDANCE_REPORT",
  "RESCHEDULE_REQUEST",
  "INQUIRY",
  "PAYMENT_REFUND_REVIEW",
  "MEMBERSHIP_SUSPENSION_EXPIRY",
  "COURSE_ENROLLMENT_REQUEST",
] as const;

export type StaffNotificationType = (typeof STAFF_NOTIFICATION_TYPES)[number];

/** 通知種別の表示名（一覧のカテゴリ列・ベルのサブテキストで使用） */
export const STAFF_NOTIFICATION_TYPE_LABELS: Record<
  StaffNotificationType,
  string
> = {
  TRIAL_APPLICATION: "体験申込",
  EVENT_APPLICATION: "イベント申込",
  EVENT_CANCELLATION: "イベントキャンセル",
  ADMISSION_REQUEST: "入会申請",
  ATTENDANCE_REPORT: "欠席・遅刻",
  RESCHEDULE_REQUEST: "振替申請",
  INQUIRY: "お問い合わせ",
  PAYMENT_REFUND_REVIEW: "返金の確認",
  MEMBERSHIP_SUSPENSION_EXPIRY: "休会期限",
  COURSE_ENROLLMENT_REQUEST: "コース追加申請",
};

/**
 * 通知種別ごとのバッジ配色（淡い背景＋濃い文字）。
 * 一覧のカテゴリ列で種別をひと目で判別できるようにする。
 */
export const STAFF_NOTIFICATION_TYPE_BADGE_CLASS: Record<
  StaffNotificationType,
  string
> = {
  TRIAL_APPLICATION: "bg-sky-100 text-sky-700",
  EVENT_APPLICATION: "bg-indigo-100 text-indigo-700",
  EVENT_CANCELLATION: "bg-slate-200 text-slate-700",
  ADMISSION_REQUEST: "bg-emerald-100 text-emerald-700",
  ATTENDANCE_REPORT: "bg-orange-100 text-orange-700",
  RESCHEDULE_REQUEST: "bg-purple-100 text-purple-700",
  INQUIRY: "bg-rose-100 text-rose-700",
  PAYMENT_REFUND_REVIEW: "bg-amber-100 text-amber-800",
  MEMBERSHIP_SUSPENSION_EXPIRY: "bg-cyan-100 text-cyan-800",
  COURSE_ENROLLMENT_REQUEST: "bg-teal-100 text-teal-800",
};

/**
 * 通知イベントの受信日時表示 (Gmail 風: 当日は時刻のみ、それ以外は日付)。
 * 通知一覧ページとヘッダーベルで共用する。
 */
export function formatStaffNotificationEventTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("ja-JP", {
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    month: "numeric",
    day: "numeric",
  });
}

/**
 * 通知イベントの既読状態が変わったことを画面間で伝えるカスタムイベント名。
 * 通知一覧ページで既読化 → ヘッダーベルのバッジを即時更新する用途。
 */
export const STAFF_NOTIFICATION_EVENTS_CHANGED =
  "staff-notification-events:changed";

/** Socketで受信した変更を、通知一覧など同一タブ内の画面へ伝える。 */
export const STAFF_NOTIFICATION_REALTIME_CHANGED =
  "staff-notification-events:realtime-changed";

export function notifyStaffNotificationEventsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STAFF_NOTIFICATION_EVENTS_CHANGED));
}
